import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IdCard, Lock, ShieldCheck } from "lucide-react";

export default function Login() {
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { employee_id: employeeId, password });
      login(data.token, data.user);
      nav("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* form */}
      <div className="flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md fade-in">
          <Logo size="lg" />
          <div className="mt-10">
            <h1 className="font-serif text-4xl tracking-tight text-navy">Sign in</h1>
            <p className="text-slate-500 mt-2 text-sm">
              Inventory, Vendor &amp; Service Management System. Internal access only.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label className="text-navy font-medium">Employee ID</Label>
              <div className="relative mt-1.5">
                <IdCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="login-employee-id-input"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder="e.g. INT0001"
                  className="pl-10 h-11"
                  autoCapitalize="characters"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-navy font-medium">Password</Label>
              <div className="relative mt-1.5">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-white px-3 py-2 rounded-lg" style={{ background: "#EF4444" }}>
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 text-white text-base hover:opacity-95 transition-opacity duration-200"
              style={{ background: "#042C53" }}
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck size={14} /> NABL-track audit environment · All actions are logged
          </div>
        </div>
      </div>

      {/* image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/10514991/pexels-photo-10514991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          alt="Laboratory"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(4,44,83,0.92), rgba(10,143,143,0.75))" }} />
        <div className="absolute bottom-0 left-0 p-12 text-white max-w-lg">
          <h2 className="font-serif text-4xl leading-tight tracking-tight">
            Full traceability from batch to bench.
          </h2>
          <p className="mt-4 text-white/80">
            FEFO-enforced stock, batch/lot expiry tracking, and role-scoped access for a regulated diagnostics lab.
          </p>
        </div>
      </div>
    </div>
  );
}
